import { useRef, useCallback, useState } from 'react';

interface VerticalSwipeOptions {
  onSwipeDown?: () => void;
  onSwipeUp?: () => void;
  threshold?: number;
}

export const useVerticalSwipe = ({
  onSwipeDown,
  onSwipeUp,
  threshold = 100
}: VerticalSwipeOptions) => {
  const touchStartY = useRef<number>(0);
  const touchStartX = useRef<number>(0);
  const isDragging = useRef<boolean>(false);
  const [dragY, setDragY] = useState<number>(0);
  const [isSwiping, setIsSwiping] = useState<boolean>(false);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartY.current = e.touches[0].clientY;
    touchStartX.current = e.touches[0].clientX;
    isDragging.current = true;
    setIsSwiping(true);
    setDragY(0);
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDragging.current) return;
    
    const currentY = e.touches[0].clientY;
    const currentX = e.touches[0].clientX;
    const deltaY = currentY - touchStartY.current;
    const deltaX = Math.abs(currentX - touchStartX.current);
    
    // Only allow swipe down for closing, with elastic resistance
    let clampedDeltaY = Math.max(0, deltaY);
    
    // Add elastic resistance when exceeding threshold
    if (clampedDeltaY > threshold) {
      const excess = clampedDeltaY - threshold;
      const resistance = Math.sqrt(excess) * 0.5; // Elastic resistance formula
      clampedDeltaY = threshold + resistance;
    }
    
    setDragY(clampedDeltaY);
    
    // Prevent horizontal scrolling if vertical swipe is detected
    if (Math.abs(deltaY) > deltaX && Math.abs(deltaY) > 10) {
      e.preventDefault();
    }
  }, [threshold]);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    if (!isDragging.current) return;
    
    const touchEndY = e.changedTouches[0].clientY;
    const touchEndX = e.changedTouches[0].clientX;
    const deltaY = touchEndY - touchStartY.current;
    const deltaX = Math.abs(touchEndX - touchStartX.current);
    
    isDragging.current = false;
    setIsSwiping(false);
    
    // Only trigger swipe if vertical movement is greater than horizontal
    if (Math.abs(deltaY) > deltaX && Math.abs(deltaY) > threshold) {
      if (deltaY > 0 && onSwipeDown) {
        onSwipeDown();
        return;
      } else if (deltaY < 0 && onSwipeUp) {
        onSwipeUp();
        return;
      }
    }
    
    // Bounce back animation if threshold not met
    setDragY(0);
  }, [onSwipeDown, onSwipeUp, threshold]);

  return {
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    dragY,
    isSwiping
  };
};